import  { useState } from 'react';
import { useTranslation } from 'react-i18next';
import useAuth from '../hooks/useAuth';
import orderFulfillmentImg from '../assets/order-fulfillment.png';
import { UI_COLORS, GRADIENTS, colorHelpers } from '../utils/colors';
import { validateEmailOrMobile } from '../utils/validate';
import ForgotPasswordForm from './ForgotPasswordForm';
import { setSurface } from '../utils/portalStorage';

// eslint-disable-next-line
const LoginForm = () => {
  // eslint-disable-next-line
  const { login, isLoading: authLoading, error: authError, clearError } = useAuth();
  const { t } = useTranslation();
  const strings = t('login', { returnObjects: true });
  const [formData, setFormData] = useState({
    identifier: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [rememberMe, setRememberMe] = useState(false);

  // Forgot password flow state
  const [isForgotPassword, setIsForgotPassword] = useState(false);

  /**
   * Controlled input change handler for login form fields.
   * - Updates `formData` state and clears any existing error for the field.
   * @param {Event} e - input change event
   */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear local error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // Clear global auth error when user starts typing
    if (authError || errors.submit) {
      setErrors(prev => ({ ...prev, submit: '' }));
      if (clearError) {
        clearError();
      }
    }
  };

  /**
   * Validate login form fields.
   * - Ensures email/mobile is present and valid.
   * @returns {boolean} true when valid
   */
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.identifier) {
      newErrors.identifier = strings.form.validation.emailRequired;
    } else {
      const emailOrMobile = validateEmailOrMobile(formData.identifier, { mobileLength: 10 });
      
      if (!emailOrMobile.isValid) {
        newErrors.identifier = strings.form.validation.emailInvalid;
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Form submit handler - performs validation and calls `login` from auth hook.
   * - Shows field or submit errors returned by the auth layer.
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    
    try {
      const result = await login(formData.identifier, formData.password, rememberMe);
      
      if (result.success) {
        // Login successful - redirect will happen automatically via auth context
      } else {
        // Login failed - show error
        setErrors({ submit: result.error });
      }
    } catch (error) {
      console.error(strings.messages.loginError, error);
      setErrors({ submit: strings.form.errors.unexpected });
    } finally {
      setIsLoading(false);
    }
  };

  /** Toggle password visibility state for the password input */
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // min-h-full rather than min-h-screen: the login page frame owns the viewport height
  // and pins the developer credit below this, so a full 100vh here would push it off.
  return (
    <div
      className="min-h-full flex items-center justify-center p-4 relative overflow-hidden"
      style={{ 
        background: GRADIENTS.NEUTRAL,
        backgroundColor: UI_COLORS.BACKGROUNDS.PRIMARY 
      }}
    >
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div 
          className="absolute -top-24 -right-24 w-48 h-48 rounded-full animate-pulse"
          style={{ backgroundColor: colorHelpers.withOpacity(UI_COLORS.BACKGROUNDS.CARD, 0.1) }}
        ></div>
        <div 
          className="absolute -bottom-32 -left-32 w-64 h-64 rounded-full animate-pulse delay-1000"
          style={{ backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.ACCENT, 0.1) }}
        ></div>
        <div 
          className="absolute top-1/2 -right-16 w-32 h-32 rounded-full animate-pulse delay-2000"
          style={{ backgroundColor: colorHelpers.withOpacity(UI_COLORS.NAVIGATION.ACTIVE, 0.1) }}
        ></div>
      </div>

      <div className="relative z-10 w-full max-w-md">
        <div 
          className="backdrop-blur-xl rounded-2xl p-8 shadow-2xl border"
          style={{ 
            backgroundColor: colorHelpers.withOpacity(UI_COLORS.BACKGROUNDS.CARD, 0.95),
            borderColor: UI_COLORS.BORDERS.LIGHT,
            boxShadow: `0 20px 25px -5px ${UI_COLORS.SHADOWS.MEDIUM}, 0 10px 10px -5px ${UI_COLORS.SHADOWS.LIGHT}`
          }}
        >
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-3 mb-6">
              <div 
                className="flex items-center justify-center w-12 h-12 rounded-xl shadow-lg p-2"
                style={{ 
                  background: GRADIENTS.PRIMARY,
                  boxShadow: `0 4px 14px 0 ${UI_COLORS.SHADOWS.COLORED}`
                }}
              >
                <img 
                  src={orderFulfillmentImg} 
                  alt={strings.brand.alt} 
                  className="w-full h-full object-contain"
                />
              </div>
              <h1 
                className="text-2xl font-bold"
                style={{ color: UI_COLORS.TEXT.PRIMARY }}
              >
                {strings.brand.name}
              </h1>
            </div>
            <h2 
              className="text-3xl font-bold mb-2"
              style={{ color: UI_COLORS.TEXT.PRIMARY }}
            >
              {isForgotPassword ? "Reset Password" : strings.header.welcome}
            </h2>
            <p style={{ color: UI_COLORS.TEXT.SECONDARY }}>
              {isForgotPassword ? "Enter your email to request a reset link" : strings.header.subtitle}
            </p>
          </div>

          {/* Audience tabs: Staff (this app) vs Customer (portal). */}
          {!isForgotPassword && (
            <div
              className="flex p-1 mb-6 rounded-xl"
              style={{ backgroundColor: colorHelpers.withOpacity(UI_COLORS.BRAND.SECONDARY, 0.06) }}
            >
              <button
                type="button"
                className="flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200"
                style={{
                  background: GRADIENTS.PRIMARY,
                  color: UI_COLORS.BUTTONS.PRIMARY.text,
                  boxShadow: `0 2px 8px 0 ${UI_COLORS.SHADOWS.COLORED}`,
                }}
              >
                {strings.tabs?.staff || 'Staff'}
              </button>
              <button
                type="button"
                onClick={() => setSurface('portal')}
                className="flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 hover:opacity-80"
                style={{ color: UI_COLORS.TEXT.SECONDARY }}
              >
                {strings.tabs?.customer || 'Customer'}
              </button>
            </div>
          )}

          {/* Form */}
          {isForgotPassword ? (
            <ForgotPasswordForm onBackToSignIn={() => setIsForgotPassword(false)} />
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Email or Mobile Field */}
              <div className="space-y-2">
                <label 
                  htmlFor="identifier" 
                  className="block text-sm font-semibold"
                  style={{ color: UI_COLORS.FORMS.LABEL }}
                >
                  {strings.form.labels.email}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <svg 
                      className="w-5 h-5" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                      style={{ color: UI_COLORS.TEXT.MUTED }}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    id="identifier"
                    name="identifier"
                    value={formData.identifier}
                    onChange={handleChange}
                    className="w-full pl-12 pr-4 py-3 border-2 rounded-xl focus:ring-2 transition-all duration-200"
                    style={{
                      backgroundColor: UI_COLORS.FORMS.INPUT.background,
                      borderColor: errors.identifier ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border,
                      color: UI_COLORS.FORMS.INPUT.text,
                      ...(errors.identifier && {
                        backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1),
                      }),
                      ...(document.activeElement === document.getElementById('identifier') && {
                        borderColor: UI_COLORS.FORMS.INPUT.focus,
                        boxShadow: `0 0 0 3px ${colorHelpers.withOpacity(UI_COLORS.FORMS.INPUT.focus, 0.1)}`
                      })
                    }}
                    placeholder={strings.form.placeholders.email}
                    autoComplete="username"
                  />
                </div>
                {errors.identifier && (
                  <p 
                    className="text-sm font-medium flex items-center gap-1"
                    style={{ color: UI_COLORS.STATUS.ERROR }}
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {errors.identifier}
                  </p>
                )}
              </div>

              {/* Password Field */}
              <div className="space-y-2">
                <label 
                  htmlFor="password" 
                  className="block text-sm font-semibold"
                  style={{ color: UI_COLORS.FORMS.LABEL }}
                >
                  {strings.form.labels.password}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <svg 
                      className="w-5 h-5" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                      style={{ color: UI_COLORS.TEXT.MUTED }}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className="w-full pl-12 pr-12 py-3 border-2 rounded-xl focus:ring-2 transition-all duration-200"
                    style={{
                      backgroundColor: UI_COLORS.FORMS.INPUT.background,
                      borderColor: errors.password ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border,
                      color: UI_COLORS.FORMS.INPUT.text,
                      ...(errors.password && {
                        backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1),
                      })
                    }}
                    placeholder={strings.form.placeholders.password}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    className="absolute inset-y-0 right-0 pr-4 flex items-center transition-colors duration-200 hover:opacity-80"
                    style={{ color: UI_COLORS.TEXT.MUTED }}
                    aria-label={showPassword ? strings.form.aria.hidePassword : strings.form.aria.showPassword}
                  >
                    {showPassword ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p 
                    className="text-sm font-medium flex items-center gap-1"
                    style={{ color: UI_COLORS.STATUS.ERROR }}
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {errors.password}
                  </p>
                )}
              </div>

              {/* Remember Me & Forgot Password Link */}
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="w-4 h-4 rounded focus:ring-2 transition-all duration-200"
                    style={{
                      accentColor: UI_COLORS.BUTTONS.PRIMARY.background,
                      borderColor: UI_COLORS.FORMS.INPUT.border
                    }}
                  />
                  <span 
                    className="text-sm font-medium group-hover:opacity-80 transition-colors duration-200"
                    style={{ color: UI_COLORS.TEXT.SECONDARY }}
                  >
                    {strings.form.rememberMe}
                  </span>
                </label>
                <button
                  type="button"
                  onClick={() => {
                    setIsForgotPassword(true);
                  }}
                  className="text-sm font-medium hover:underline transition-all duration-200 focus:outline-none"
                  style={{ color: UI_COLORS.TEXT.ACCENT }}
                >
                  Forgot password?
                </button>
              </div>

              {/* Submit Error */}
              {(errors.submit || authError) && (
                <div 
                  className="border px-4 py-3 rounded-xl text-sm font-medium text-center"
                  style={{
                    backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1),
                    borderColor: UI_COLORS.STATUS.ERROR,
                    color: UI_COLORS.STATUS.ERROR
                  }}
                >
                  {errors.submit || authError}
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading || authLoading}
                className="w-full py-3 px-6 rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none"
                style={{
                  background: isLoading || authLoading 
                    ? UI_COLORS.BUTTONS.PRIMARY.disabled 
                    : GRADIENTS.PRIMARY,
                  color: UI_COLORS.BUTTONS.PRIMARY.text,
                  boxShadow: `0 4px 14px 0 ${UI_COLORS.SHADOWS.COLORED}`
                }}
                onMouseEnter={(e) => {
                  if (!isLoading && !authLoading) {
                    e.target.style.background = GRADIENTS.WARM;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isLoading && !authLoading) {
                    e.target.style.background = GRADIENTS.PRIMARY;
                  }
                }}
              >
                {isLoading || authLoading ? (
                  <div className="flex items-center justify-center gap-2">
                    <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {strings.form.submit.loading}
                  </div>
                ) : (
                  strings.form.submit.default
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginForm;
